// Notification Service
// Handles fetching and managing notifications from the API
import { getApiBaseUrl } from '../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

/**
 * Get authentication headers
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/**
 * Get all notifications for the current user
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of notifications to fetch
 * @param {number} options.offset - Offset for pagination
 * @param {boolean} options.unread_only - Only fetch unread notifications
 */
export const getUserNotifications = async (options = {}) => {
  try {
    const { limit = 50, offset = 0, unread_only = false } = options;
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      unread_only: unread_only.toString()
    });
    
    const response = await fetch(`${API_BASE_URL}/notifications?${params}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = new Error(`Failed to fetch notifications: ${response.statusText}`);
      error.status = response.status;
      error.isAuthError = response.status === 401 || response.status === 403;
      throw error;
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Get unread notification count
 */
export const getUnreadNotificationCount = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/notifications/unread-count`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = new Error(`Failed to fetch unread count: ${response.statusText}`);
      error.status = response.status;
      error.isAuthError = response.status === 401 || response.status === 403;
      throw error;
    }
    
    const data = await response.json();
    return data.count || 0;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    // Return 0 for auth errors to prevent UI issues, but still throw to stop polling
    if (error.isAuthError) {
      throw error;
    }
    return 0;
  }
};

/**
 * Mark a notification as read
 * @param {number} notificationId - The notification ID
 */
export const markNotificationRead = async (notificationId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}/read`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Failed to mark notification as read: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read for the current user
 */
export const markAllNotificationsRead = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/notifications/read-all`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Failed to mark all notifications as read: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

